// hooks/NewCaseView/useCaseSubmission.js

import { useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useHistory } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { Endpoint } from "../../api/base";
import { toSavedSubjects } from "../../components/shared/helpers/newCaseInitValues";

/**
 * The representative to save to the client — a new contact, or an existing one
 * with an edited phone — or null when the client already matches the form.
 */
export const representativeToSave = (values, clientList) => {
  const client = clientList.find((c) => c.client_id === values.client_id);
  const id = values.case_details.assigment_information.supervisor_name;
  const phone = values.case_details.client_information.phone_number;
  const contacts = client?.contacts ?? [];
  const saved = contacts.find((c) => c.id === id);
  const added = values.new_representative?.id === id && !saved;
  if (!client || (!added && (!saved || saved.phone === phone))) return null;

  return { ...(added ? values.new_representative : saved), phone };
};

/**
 * Hook to handle case submission logic
 */
export const useCaseSubmission = () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Get user ID from Cognito token
   */
  const getUserId = () => {
    const userName = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_CLIENT_ID}.LastAuthUser`,
    );
    const token = localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_CLIENT_ID}.${userName}.idToken`,
    );

    if (!token) {
      throw new Error("No authentication token found");
    }

    const decoded = jwt_decode(token);
    return decoded.sub;
  };

  /**
   * Format property address as string
   */
  const formatPropertyAddress = (address) => {
    return `${address.street_address} ${address.city} ${address.state} ${address.zip_code}`;
  };

  /**
   * Format case data for submission
   */
  const formatCaseData = (value) => {
    const userId = getUserId();

    // Direct mutation
    value.flag = false;
    value.user_id = userId;
    delete value.new_representative;

    value.case_details.Subjects_information = toSavedSubjects(
      value.case_details.Subjects_information,
    );
    const primary = value.case_details.Subjects_information[0];
    const isBusiness = primary.subject_type === "business";

    // The case list, search and invoices read the name from these copies.
    value.case_details.case_information.subject_first_name = isBusiness
      ? primary.business_name
      : primary.first_name;

    value.case_details.case_information.subject_last_name = isBusiness
      ? ""
      : primary.last_name;

    value.case_details.case_information.subject_property_address =
      formatPropertyAddress(
        value.case_details.case_information.subject_property_address,
      );

    value.case_details.case_information.city =
      value.case_details.Subjects_information[0].city;

    value.case_details.case_information.death_certificate_ordered =
      primary.death_cert_order_date ?? "";

    value.case_details.case_information.death_certificate_received =
      primary.death_cert_received_date ?? "";

    value.primary_subject =
      value.case_details.Subjects_information[0].subject_id;

    value.case_details.client_information.date_of_sale =
      value.case_details.case_information.sale_date;

    value.case_details.client_information.follow_up =
      value.case_details.case_information.follow_up;

    value.case_details.case_information.create_at = new Date();
    value.case_details.case_information.file_due = true;

    return value;
  };

  /**
   * Submit case to API (matches your exact current flow)
   */
  const submitCase = async (values, clientList) => {
    setIsSubmitting(true);

    // The case stores only the contact id, so the contact must exist first.
    const contact = representativeToSave(values, clientList);
    if (contact) {
      try {
        await Endpoint.updateClient({ client_id: values.client_id, contact });
      } catch (error) {
        console.error(error);
        showToast(
          "Couldn't save the client representative. The case was not created.",
          "error",
        );
        setIsSubmitting(false);
        throw error;
      }
    }

    try {
      const formattedData = formatCaseData(values);

      const response = await Endpoint.submitCase(JSON.stringify(formattedData));

      showToast("Case added successfully", "success");
      history.push(`/app/cases/${response.data.file_no}/case`);

      return response;
    } catch (error) {
      console.error(error);
      showToast("Sorry something went wrong, please try again later", "error");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitCase,
    isSubmitting,
  };
};

export default useCaseSubmission;
